import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { blogPosts } from '@/mocks/blog';
import SeoHead from '@/components/SeoHead';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';
import { getRecommendedProjectForCategory, resolveSmartProjectUrl } from '@/data/flagshipProjects';
import { renderLinkedText } from '@/utils/internalLinker';

export default function BlogPostPage() {
  const { slug } = useParams<{ slug: string }>();
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const isAr = lang === 'ar';

  const post = blogPosts.find((p) => p.slug === slug);

  if (!post) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <Navbar />
        <main className="flex-grow flex items-center justify-center pt-28">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              {isAr ? 'المقال غير موجود' : 'Article Not Found'}
            </h1>
            <p className="text-gray-600 mb-6">
              {isAr ? 'المقال الذي تبحث عنه غير متوفر.' : 'The article you are looking for does not exist.'}
            </p>
            <Link
              to="/blog"
              className="inline-flex items-center bg-[rgb(0,0,139)] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[rgb(0,0,100)] transition-all cursor-pointer"
            >
            <i className={isAr ? 'ri-arrow-left-line mr-2' : 'ri-arrow-right-line mr-2'}></i>
              {isAr ? 'العودة إلى المدونة' : 'Back to Blog'}
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const title = isAr ? post.titleAr : post.title;
  const excerpt = isAr ? post.excerptAr : post.excerpt;
  const content = isAr ? post.contentAr : post.content;
  const category = isAr ? post.categoryAr : post.category;
  const readTime = isAr ? post.readTimeAr : post.readTime;
  const recommendedProject = getRecommendedProjectForCategory(post.category);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: title,
    description: excerpt,
    image: post.image,
    datePublished: post.date,
    dateModified: post.date,
    author: {
      '@type': 'Organization',
      name: 'Debug',
      url: 'https://debuging.com/',
    },
    publisher: {
      '@type': 'Organization',
      name: 'Debug',
      logo: {
        '@type': 'ImageObject',
        url: 'https://static.readdy.ai/image/c83f6350fa782ce29b612b38d2cee1d4/3b7550d6c06eea93242f62b6efa2ff71.png',
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://debuging.com/blog/${post.slug}`,
    },
    keywords: post.keywords,
  };

  const relatedPosts = blogPosts.filter((p) => p.slug !== post.slug).slice(0, 3);

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: isAr ? 'الرئيسية' : 'Home',
        item: 'https://debuging.com/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: isAr ? 'المدونة' : 'Blog',
        item: 'https://debuging.com/blog',
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: title,
        item: `https://debuging.com/blog/${post.slug}`,
      },
    ],
  };

  return (
    <div className="min-h-screen bg-white">
      <SeoHead
        title={title}
        description={excerpt}
        keywords={post.keywords}
        canonical={`https://debuging.com/blog/${post.slug}`}
        ogImage={post.image}
        ogType="article"
        lang={lang}
      />
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>

      <header>
        <Navbar />
      </header>

      <main className="pt-28 pb-20">
        <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" itemScope itemType="https://schema.org/BlogPosting">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm text-gray-500 mb-8" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-[rgb(0,0,139)] transition-colors">{t('home')}</Link>
            <i className={isAr ? 'ri-arrow-left-line text-xs' : 'ri-arrow-right-line text-xs'}></i>
            <Link to="/blog" className="hover:text-[rgb(0,0,139)] transition-colors">
              {isAr ? 'المدونة' : 'Blog'}
            </Link>
            <i className={isAr ? 'ri-arrow-left-line text-xs' : 'ri-arrow-right-line text-xs'}></i>
            <span className="text-gray-900 font-medium truncate">{title}</span>
          </nav>

          {/* Header */}
          <header className="mb-10">
            <div className="flex items-center gap-3 mb-4 text-sm">
              <span className="bg-[rgb(0,0,139)]/10 text-[rgb(0,0,139)] px-3 py-1 rounded-full font-medium">
                {category}
              </span>
              <span className="text-gray-500">{post.date}</span>
              <span className="text-gray-500">{readTime}</span>
            </div>
            <h1 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight" itemProp="headline">
              {title}
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed" itemProp="description">
              {excerpt}
            </p>
          </header>

          {/* Featured Image */}
          <div className="w-full h-64 md:h-96 rounded-2xl overflow-hidden mb-12">
            <img
              src={post.image}
              alt={title}
              title={title}
              className="w-full h-full object-cover"
              itemProp="image"
              loading="eager"
            />
          </div>

          {/* Content */}
          <div className="prose prose-lg max-w-none" itemProp="articleBody">
            {content.map((section, idx) => (
              <section key={idx} className="mb-10">
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                  {section.heading}
                </h2>
                <p className="text-gray-700 leading-relaxed text-lg">
                  {renderLinkedText(section.body, isAr)}
                </p>
              </section>
            ))}
          </div>

          {/* Contextual Flagship Recommendation / Smart In-Blog Routing */}
          {recommendedProject && (
            <div className="mt-14 bg-gradient-to-r from-blue-50/70 to-indigo-50/70 border border-blue-200/80 rounded-2xl p-6 md:p-8 shadow-sm">
              <div className="flex flex-col sm:flex-row gap-6 items-center">
                <div className="w-full sm:w-44 h-36 rounded-xl overflow-hidden bg-white shadow-sm flex-shrink-0 border border-gray-200">
                  <img
                    src={recommendedProject.image}
                    alt={isAr ? recommendedProject.titleAr : recommendedProject.title}
                    className="w-full h-full object-cover object-top"
                    loading="lazy"
                  />
                </div>
                <div className="flex-1 text-start">
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <span className="bg-[rgb(0,0,139)] text-white text-xs font-bold px-3 py-1 rounded-full shadow-xs">
                      {t('recommendedProductBadge')}
                    </span>
                    <span className="bg-white text-gray-700 text-xs font-semibold px-2.5 py-1 rounded-full border border-gray-200">
                      {recommendedProject.brand}
                    </span>
                  </div>
                  <h4 className="text-xl font-bold text-gray-900 mb-2">
                    {isAr ? recommendedProject.titleAr : recommendedProject.title}
                  </h4>
                  <p className="text-gray-600 text-sm leading-relaxed mb-4">
                    {isAr ? recommendedProject.descriptionAr : recommendedProject.description}
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <button
                      onClick={() => {
                        const url = resolveSmartProjectUrl(recommendedProject);
                        if (url && url !== '#') {
                          window.open(url, '_blank', 'noopener,noreferrer');
                        }
                      }}
                      className="inline-flex items-center bg-[rgb(0,0,139)] text-white px-5 py-2.5 rounded-lg text-sm font-semibold hover:bg-[rgb(0,0,100)] transition-all cursor-pointer shadow-sm"
                    >
                      <span>{t('launchSystem')}</span>
                      <i className={isAr ? 'ri-arrow-left-line mr-2' : 'ri-arrow-right-line ml-2'}></i>
                    </button>
                    {recommendedProject.webUrl && (
                      <a
                        href={recommendedProject.webUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-gray-700 hover:text-[rgb(0,0,139)] px-4 py-2.5 rounded-lg text-sm font-semibold bg-white border border-gray-200 hover:border-[rgb(0,0,139)] transition-all"
                      >
                        <i className="ri-global-line mr-1.5 rtl:ml-1.5 rtl:mr-0"></i>
                        {t('visitWebPlatform')}
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* CTA Box */}
          <div className="bg-gradient-to-br from-[rgb(0,0,139)] to-[rgb(0,0,100)] rounded-2xl p-8 md:p-10 mt-10 text-white">
            <h3 className="text-2xl font-bold mb-3">
              {isAr ? 'هل تحتاج إلى مساعدة خبيرة؟' : 'Need Expert Help?'}
            </h3>
            <p className="text-white/90 mb-6 text-lg">
              {isAr
                ? 'فريق Debug جاهز لمساعدتك في مشاريع التطوير والتصحيح. دعنا نناقش احتياجاتك.'
                : 'The Debug team is ready to help with your development and debugging projects. Let\'s discuss your needs.'}
            </p>
            <Link
              to="/"
              onClick={() => {
                setTimeout(() => {
                  const el = document.getElementById('contact');
                  if (el) el.scrollIntoView({ behavior: 'smooth' });
                }, 100);
              }}
              className="inline-flex items-center bg-white text-[rgb(0,0,139)] px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-all cursor-pointer"
            >
              {isAr ? 'اتصل بنا' : 'Contact Us'}
              <i className={isAr ? 'ri-arrow-left-line ml-2' : 'ri-arrow-right-line ml-2'}></i>
            </Link>
          </div>

          {/* Meta tags for schema */}
          <meta itemProp="datePublished" content={post.date} />
          <meta itemProp="dateModified" content={post.date} />
          <span itemProp="author" itemScope itemType="https://schema.org/Organization">
            <meta itemProp="name" content="Debug" />
          </span>
          <span itemProp="publisher" itemScope itemType="https://schema.org/Organization">
            <meta itemProp="name" content="Debug" />
          </span>
        </article>

        {/* Related Posts */}
        {relatedPosts.length > 0 && (
          <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-20">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">
              {isAr ? 'مقالات ذات صلة' : 'Related Articles'}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {relatedPosts.map((rp) => (
                <article
                  key={rp.slug}
                  className="bg-gray-50 rounded-xl p-6 hover:bg-gray-100 transition-colors"
                >
                  <span className="text-[rgb(0,0,139)] text-sm font-medium">
                    {isAr ? rp.categoryAr : rp.category}
                  </span>
                  <h3 className="text-lg font-bold text-gray-900 mt-2 mb-3">
                    <Link to={`/blog/${rp.slug}`} className="hover:text-[rgb(0,0,139)] transition-colors">
                      {isAr ? rp.titleAr : rp.title}
                    </Link>
                  </h3>
                  <p className="text-gray-600 text-sm line-clamp-3">
                    {isAr ? rp.excerptAr : rp.excerpt}
                  </p>
                </article>
              ))}
            </div>
          </section>
        )}
      </main>

      <footer>
        <Footer />
      </footer>
    </div>
  );
}